import { cs } from '../../../lib/currency';
/**
 * Manual Invoice Dialog (iter220, Feb 2026).
 *
 * Admin flow:
 *   1. Search + pick a customer (parent or plain customer user).
 *   2. Pick invoice type (Enrollment / Tournament / Coaching /
 *      Small Group / Lane Booking).
 *   3. Optionally pick one of the customer's active items of that
 *      type — autofills line-item name + base amount.
 *   4. Override name / amount / add a discount as needed.
 *   5. Submit → POST /api/admin/invoices/manual-create.
 *
 * The created invoice persists into a dedicated `manual_invoices`
 * collection. Invoice Management surfaces these rows (filter type
 * "Manual") so the same UI handlers — mark-paid, edit, void, charge
 * card, reminders — work uniformly.
 */
import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../../ui/select';
import { Badge } from '../../ui/badge';
import { Loader2, Search, FileText } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../services/api';

const INVOICE_TYPES = [
  { value: 'enrollment',   label: 'Enrollment (Monthly)' },
  { value: 'tournament',   label: 'Tournament' },
  { value: 'coaching',     label: 'Coaching (1:1)' },
  { value: 'small_group',  label: 'Small Group' },
  { value: 'lane_booking', label: 'Lane Booking' },
];

export default function ManualInvoiceDialog({ open, onOpenChange, onCreated }) {
  // Customer search + selection.
  const [customerQuery, setCustomerQuery] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [customerSearching, setCustomerSearching] = useState(false);
  const [customer, setCustomer] = useState(null);  // { id, name, email, type }

  // Invoice type + auto-detected billable items.
  const [invoiceType, setInvoiceType] = useState('enrollment');
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState('');

  // Line item fields.
  const [itemName, setItemName] = useState('');
  const [baseAmount, setBaseAmount] = useState('');
  const [discountAmount, setDiscountAmount] = useState('');
  const [discountReason, setDiscountReason] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');

  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setCustomerQuery(''); setCustomerResults([]); setCustomer(null);
    setInvoiceType('enrollment'); setItems([]); setSelectedItemId('');
    setItemName(''); setBaseAmount(''); setDiscountAmount('');
    setDiscountReason(''); setDueDate(''); setNotes('');
  };

  useEffect(() => { if (open) reset(); }, [open]);

  // Customer search — debounced via the search button. Uses the
  // existing /admin/global-search endpoint which already filters to
  // parents + customers when the caller is admin.
  const searchCustomers = useCallback(async () => {
    if (!customerQuery || customerQuery.length < 2) return;
    setCustomerSearching(true);
    try {
      const res = await api.get('/admin/global-search', { params: { q: customerQuery } });
      // Keep only parent / customer hits for this dialog.
      const rows = (res.data?.results || []).filter(r => r.type === 'parent' || r.type === 'customer');
      setCustomerResults(rows);
    } catch (e) {
      toast.error('Search failed');
    } finally {
      setCustomerSearching(false);
    }
  }, [customerQuery]);

  // Whenever invoice type or customer changes, reload the
  // autodetected billable items.
  useEffect(() => {
    if (!customer?.id || !invoiceType) {
      setItems([]); setSelectedItemId(''); return;
    }
    setItemsLoading(true);
    api.get(`/admin/billable-items/${customer.id}`, { params: { type: invoiceType } })
      .then(res => {
        setItems(res.data?.items || []);
      })
      .catch(() => setItems([]))
      .finally(() => setItemsLoading(false));
  }, [customer?.id, invoiceType]);

  const onPickItem = (id) => {
    setSelectedItemId(id);
    const it = items.find(i => i.id === id);
    if (it) {
      // Autofill — admin can still override.
      setItemName(it.label);
      setBaseAmount(String(it.amount));
    }
  };

  const total = (() => {
    const base = parseFloat(baseAmount || '0');
    const disc = parseFloat(discountAmount || '0');
    return Math.max(0, base - disc).toFixed(2);
  })();

  const handleSubmit = async () => {
    if (!customer?.id) { toast.error('Pick a customer'); return; }
    if (!itemName.trim()) { toast.error('Item name is required'); return; }
    const base = parseFloat(baseAmount);
    if (!base || base <= 0) { toast.error('Amount must be > 0'); return; }
    const disc = parseFloat(discountAmount || '0') || 0;
    if (disc < 0 || disc > base) { toast.error('Discount must be 0 to base amount'); return; }
    if (disc > 0 && !discountReason.trim()) {
      toast.error('Discount reason is required when a discount is applied'); return;
    }

    const selectedItem = items.find(i => i.id === selectedItemId);

    setSubmitting(true);
    try {
      await api.post('/admin/invoices/manual-create', {
        customer_id: customer.id,
        invoice_type: invoiceType,
        item_name: itemName.trim(),
        base_amount: base,
        discount_amount: disc,
        discount_reason: discountReason.trim() || null,
        due_date: dueDate || null,
        notes: notes.trim() || null,
        source_entity_id: selectedItem?.id || null,
        source_entity_summary: selectedItem?.label || null,
      });
      toast.success('Invoice created');
      onOpenChange(false);
      onCreated && onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create invoice');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="manual-invoice-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-emerald-600" />
            Create Manual Invoice
          </DialogTitle>
          <DialogDescription>
            Ad-hoc invoices for any parent or customer. Persists to Invoice Management for normal collection.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* 1. Customer picker */}
          {!customer ? (
            <div className="space-y-2">
              <Label>Customer / Parent</Label>
              <div className="flex gap-2">
                <Input
                  value={customerQuery}
                  onChange={(e) => setCustomerQuery(e.target.value)}
                  placeholder="Search by name, email, or numeric ID…"
                  onKeyDown={(e) => e.key === 'Enter' && searchCustomers()}
                  data-testid="manual-invoice-customer-search"
                />
                <Button onClick={searchCustomers} disabled={customerSearching || customerQuery.length < 2}>
                  {customerSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                </Button>
              </div>
              {customerResults.length > 0 && (
                <div className="border rounded-md divide-y max-h-48 overflow-y-auto" data-testid="customer-search-results">
                  {customerResults.map(r => (
                    <button
                      key={r.id}
                      onClick={() => setCustomer({ id: r.id, name: r.name, email: r.email, type: r.type })}
                      className="w-full text-left p-2 hover:bg-slate-50 text-sm"
                      data-testid={`customer-result-${r.id}`}
                    >
                      <div className="font-medium text-slate-900 flex items-center gap-2">
                        {r.name}
                        <Badge variant="outline" className="text-[10px] capitalize">{r.type}</Badge>
                      </div>
                      <div className="text-xs text-slate-500">{r.email}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="p-3 rounded-lg border bg-slate-50 flex items-start justify-between gap-2" data-testid="selected-customer-block">
              <div>
                <div className="text-xs text-slate-500">Recipient</div>
                <div className="font-medium text-slate-900 flex items-center gap-2">
                  {customer.name}
                  <Badge variant="outline" className="text-[10px] capitalize">{customer.type}</Badge>
                </div>
                <div className="text-xs text-slate-500">{customer.email}</div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => setCustomer(null)} data-testid="change-customer-btn">
                Change
              </Button>
            </div>
          )}

          {/* 2. Type + autodetected item */}
          {customer && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Invoice Type</Label>
                  <Select value={invoiceType} onValueChange={setInvoiceType}>
                    <SelectTrigger data-testid="invoice-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {INVOICE_TYPES.map(t => (
                        <SelectItem key={t.value} value={t.value} data-testid={`invoice-type-${t.value}`}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>
                    Existing Item
                    {itemsLoading && <Loader2 className="inline h-3 w-3 ml-1 animate-spin text-slate-400" />}
                  </Label>
                  <Select value={selectedItemId} onValueChange={onPickItem} disabled={items.length === 0}>
                    <SelectTrigger data-testid="billable-item-select">
                      <SelectValue placeholder={items.length === 0 ? 'No active items for this type' : 'Optional — pick to autofill'} />
                    </SelectTrigger>
                    <SelectContent>
                      {items.map(it => (
                        <SelectItem key={it.id} value={it.id} data-testid={`billable-item-${it.id}`}>
                          {it.label} · {cs()}{Number(it.amount).toFixed(2)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* 3. Line item details */}
              <div className="space-y-2">
                <Label>Item Name <span className="text-red-500">*</span></Label>
                <Input
                  value={itemName}
                  onChange={(e) => setItemName(e.target.value)}
                  placeholder="e.g. April Coaching Block, Lane Rental – Sat 9 AM…"
                  data-testid="manual-invoice-item-name"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Base Amount ($) <span className="text-red-500">*</span></Label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={baseAmount}
                    onChange={(e) => setBaseAmount(e.target.value)}
                    placeholder="0.00"
                    data-testid="manual-invoice-base-amount"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Discount ($) <span className="text-xs text-slate-400">— optional</span></Label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={discountAmount}
                    onChange={(e) => setDiscountAmount(e.target.value)}
                    placeholder="0.00"
                    data-testid="manual-invoice-discount-amount"
                  />
                </div>
              </div>

              {parseFloat(discountAmount || '0') > 0 && (
                <div className="space-y-2">
                  <Label>Discount Reason <span className="text-red-500">*</span></Label>
                  <Input
                    value={discountReason}
                    onChange={(e) => setDiscountReason(e.target.value)}
                    placeholder="e.g. Sibling discount, Promo code, Goodwill credit…"
                    data-testid="manual-invoice-discount-reason"
                  />
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <Input
                    type="date" value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    data-testid="manual-invoice-due-date"
                  />
                  <p className="text-xs text-slate-500">Empty = 7 days from today.</p>
                </div>
                <div className="space-y-2">
                  <Label>Total</Label>
                  <div className="border rounded-md px-3 py-2 bg-emerald-50 border-emerald-200 font-semibold text-emerald-700 text-lg" data-testid="manual-invoice-total">
                    {cs()}{total}
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notes <span className="text-xs text-slate-400">— optional</span></Label>
                <Textarea
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any internal note that should appear on the invoice…"
                  data-testid="manual-invoice-notes"
                />
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="manual-invoice-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !customer || !itemName.trim() || !baseAmount}
            data-testid="manual-invoice-submit-btn"
          >
            {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating…</> : 'Create Invoice'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
